
import gql from 'graphql-tag';
import productPromotionFragment from '~/apollo/fragments/productPromotionFragment';
import medicalPlanScenariosQuery from '~/apollo/queries/product/medicalPlanScenario.gql';
import medicalTooltips from '~/apollo/queries/product/tooltips/medicalTooltips';
import commonProductDetailMixins from '~/mixins/commonProductDetailMixins.js';
import productPromotionMixins from '~/mixins/productPromotionMixins.js';
import productShortcutMixins from "~/mixins/productShortcutMixins.js";
import productInsuranceNameMixins from '~/mixins/productInsuranceNameMixins';

export default {
  mixins: [commonProductDetailMixins, productPromotionMixins, productShortcutMixins, productInsuranceNameMixins],

  data() {
    return {
      medicalPlanScenarios: null,
      blogDisclaimers: null,
      loading: 0,
      age: this.$route.query.age || '35',
      gender: this.$route.query.gender || '1',
      currentLocale: this.$normalizeLocale(this.$i18n.locale),
    };
  },
  // page component definitions
  apollo: {
    $loadingKey: 'loading',
    medicalPlanScenarios: {
      query: gql`
        ${medicalPlanScenariosQuery}
        ${productPromotionFragment}
      `,
      prefetch: true,
      variables() {
        return {
          scenario_code: this.$route.params.slug,
          locale: this.currentLocale,
        };
      },
      // below handling will result in vue hydration error..
      // placing error-component inside ProductDetailWrapper as a workaround.
      // result(result) {
      //   if (!result.data.medicalPlanScenarios?.data[0])
      //     return this.$nuxt.error({ statusCode: 404, message: 'Product not found' });
      // },
    },
    medicalTooltips: {
      query: medicalTooltips,
      prefetch: true,
      variables() {
        return { currentlocale: this.$normalizeLocale(this.$i18n.locale) };
      },
      update(data) {
        return this.$normalizeStrapi(data.infoTooltip.data);
      },
    },
  },
  computed: {
    simpleItems() {
      if (this.medicalPlanScenarios?.data[0]) {
        const item = this.medicalPlanScenarios.data[0];
        let promotionPrices = Array;
        promotionPrices = [];

        let discountedTenYearAvgPremium = null;
        const age = parseInt(this.age);
        let promotions = null;
        let promotion = null;
        let promotionLastCall = null;

        const planName = item.attributes.medical_plan.data.attributes.medical_plan_translations.data[0].attributes.name;
        let decoderCTALink = '';
        // let decoderCTAText = '';

        let mainCTA = this.getDecoderCTA(item, decoderCTALink);

        const shortcut = this.getProductShortcut(item.attributes);

        // check scenario level promo first , then plan level promotion
        if (item.attributes.product_promotions.data?.length > 0) {
          promotions = item.attributes.product_promotions.data;
        }
        if (item.attributes.medical_plan.data.attributes.product_promotions.data?.length > 0) {
          promotions = item.attributes.medical_plan.data.attributes.product_promotions.data;
        }

        if(promotions != null) {
          promotion = this.checkPromotionValid(promotions);
          if (!promotion) {
            promotion = null;
          } else {
            if (promotion.attributes?.product_promotion_translations?.data[0]?.attributes?.DecoderCTALink) {
              decoderCTALink =
                promotion.attributes?.product_promotion_translations?.data[0]?.attributes?.DecoderCTALink;
              // decoderCTAText = this.$t('products.product_website');getDecoderCTA
            }

            mainCTA = this.getDecoderCTA(item, decoderCTALink, promotion);

            const discounts = promotion.attributes.DiscountPercentage;
            promotionLastCall = this.checkPromotionLastCall(promotion);

            if (discounts) {
              // calculate promotion price and avg premium
              let tenYearPremium = 0;
              const premium = item.attributes.medical_plan_premium_details;
              const premiumArray = this.getPremium(premium, age);

              premiumArray.forEach((premium) => {
                const i = premium.age - age;

                if (discounts['Year' + parseInt(i + 1)] === 0) {
                  promotionPrices[premium.age] = null;
                  tenYearPremium += premium.value;
                } else {
                  const calculatedPrice = (premium.value * (100 - discounts['Year' + parseInt(i + 1)])) / 100;
                  promotionPrices[premium.age] = parseInt(calculatedPrice.toFixed(0));
                  tenYearPremium += calculatedPrice;
                }
              });

              discountedTenYearAvgPremium = parseInt((tenYearPremium.toFixed(0) / premiumArray.length).toFixed(0));
            }
          }
        }
        // console.log("END PROMO");

        const averagePremium = item.attributes.medical_plan_premium_details.data.filter((agePremium) => {
          return agePremium.attributes.age === age;
        })[0].attributes.average_premium;
        if (discountedTenYearAvgPremium === parseInt(averagePremium.toFixed(0))) {
          discountedTenYearAvgPremium = null;
        }

        const filteredTranslations = this.getFilteredTranslations(item.attributes.medical_plan.data.attributes.product.data.attributes.insurance_company.data.attributes
        .insurance_company_translations.data[0].attributes.localizations.data);

        return {
          // ***** OVERVIEW *****
          scenario_code: item.attributes.scenario_code,
          overall_score: item.attributes.medical_plan.data.attributes.overall_score,
          special_feature_score: item.attributes.medical_plan.data.attributes.special_feature_score,
          medical_coverage_score: item.attributes.medical_plan.data.attributes.medical_coverage_score,
          medical_coverage: item.attributes.medical_plan.data.attributes.medical_coverage,
          medical_cancer_coverage: item.attributes.medical_plan.data.attributes.medical_cancer_coverage,
          // medical_inflation_protection: item.attributes.medical_plan.data.attributes.medical_inflation_protection,
          // additional_features: item.attributes.medical_plan.data.attributes.additional_features,
          deductible: item.attributes.medical_plan.data.attributes.deductible,
          base_plan_annual_limit:
            item.attributes.medical_plan.data.attributes.medical_plan_translations.data[0].attributes
              .base_plan_annual_limit,
          lifetime_limit:
            item.attributes.medical_plan.data.attributes.medical_plan_translations.data[0].attributes.lifetime_limit,
          include_smm: item.attributes.medical_plan.data.attributes.include_smm,
          smm_co_pay:
            item.attributes.medical_plan.data.attributes.medical_plan_translations.data[0].attributes.smm_co_pay,
          smm_general_limits:
            item.attributes.medical_plan.data.attributes.medical_plan_translations.data[0].attributes
              .smm_general_limits,
          smm_lifetime_limits:
            item.attributes.medical_plan.data.attributes.medical_plan_translations.data[0].attributes
              .smm_lifetime_limits,
          currency: item.attributes.medical_plan.data.attributes.currency.data.attributes.code,

          // ***** Estimated Coverage of selected Diseases *****
          medical_plan_coverages: item.attributes.medical_plan.data.attributes.medical_plan_coverages.data,

          // ***** Benefit Limits (Base Plan) *****
          medical_ben_limit_values: item.attributes.medical_plan.data.attributes.medical_ben_limit_values.data,

          // ***** Supplementary Major Medical Benefits *****
          medical_smm_values: item.attributes.medical_plan.data.attributes.medical_smm_values.data,

          // ***** Medical Inflation Protection *****
          // medical_inflation_values: item.attributes.medical_plan.data.attributes.medical_inflation_values.data,

          // ***** Additional Features *****
          // medical_add_feature_values: item.attributes.medical_plan.data.attributes.medical_add_feature_values.data,

          // ***** Special Features *****
          medical_special_features: item.attributes.medical_plan.data.attributes.medical_special_features.data,

          // ***** PREMIUM *****
          premium: item.attributes.medical_plan_premium_details.data,
          fyp: item.attributes.medical_plan_premium_details.data.filter((agePremium) => {
            return agePremium.attributes.age === parseInt(this.age);
          })[0].attributes.price,
          average_premium: averagePremium,

          // ***** Insurer *****
          insurer_name_first:
            item.attributes.medical_plan.data.attributes.product.data.attributes.insurance_company.data.attributes
              .insurance_company_translations.data[0].attributes.name,
          insurer_name_second:
            filteredTranslations[0].attributes.name,
          insurer_logo:
            item.attributes.medical_plan.data.attributes.product.data.attributes.insurance_company.data.attributes.logo,
          insurer_code:
            item.attributes.medical_plan.data.attributes.product.data.attributes.insurance_company.data.attributes.code,

          // ***** Plan *****
          plan_name: planName,
          overall_star: item.attributes.medical_plan.data.attributes.overall_star,

          // ***** Feature *****
          room: item.attributes.medical_plan.data.attributes.medical_room.data.attributes.medical_room_translations
            .data[0].attributes.name,
          geo_region:
            item.attributes.medical_plan.data.attributes.medical_geo_region.data.attributes
              .medical_geo_region_translations.data[0].attributes.name,
          vhis_type:
            item.attributes.medical_plan.data.attributes.medical_vhis_type.data.attributes
              .medical_vhis_type_translations.data[0].attributes.name,

          // ***** max_issue_age *****
          max_issue_age: item.attributes.medical_plan.data.attributes.max_issue_age,

          // ***** PROMOTION *****
          product_promotion: promotion,
          promotion_prices: promotionPrices,
          promotion_last_call: promotionLastCall,

          discounted_ten_year_avg_premium: discountedTenYearAvgPremium,
          main_cta: mainCTA,
          shortcut: shortcut,
          scenarioId: item.id,
        };
      }
      return null;
    },
    product() {
      const item = this.simpleItems;

      if (!item) return null;

      return {
        highlightSection: {
          scenarioCode: item.scenario_code,
          scenarioId: item.scenarioId,
          showFiveStarBadge: this.$formatShowFiveStarBadge(item.overall_star),
          productName: item.plan_name,
          insuranceCompany: {
            name: item.insurer_name_first + ' | ' + item.insurer_name_second,
            code: item.insurer_code,
            logoSrc: item.insurer_logo?.data?.attributes?.url,
            logoAlt: item.insurer_name_first,
          },
          userRating: {
            score: item.overall_star,
            numberOfRatedUser: '0',
          },
          tenlifeScore: {
            score: parseFloat(this.getScore(item)),
            description: this.$t('products.medical.md_overall_score'),
            toolTipHtml: this.getTooltipContentByKey('md_overall_score'),
          },
          features: [
            {
              text: item.room,
              type: 'room_type',
            },
            {
              text: item.geo_region,
              type: 'geographical_coverage',
            },
            {
              text: item.vhis_type,
              type: 'plan_type',
            },
            {
              text: this.$formatPrice(item.deductible) + ' ' + this.$t('products.medical.md_deductible'),
              type: 'deductible',
            },
          ],
          keyItems: [
            {
              value: this.$formatScore(item.medical_coverage_score) + ' / 10',
              key: this.$t('products.medical.md_medicalCoverageScore'),
              topSubText: '',
              toolTipHtml: this.getTooltipContentByKey('md_medicalCoverageScore'),
              symbolValue: '',
              originalValue: '',
            },
            {
              value: this.$formatScore(item.special_feature_score) + ' / 10',
              key: this.$t('products.medical.md_special_feature_score'),
              topSubText: '',
              toolTipHtml: this.getTooltipContentByKey('md_special_feature_score'),
              symbolValue: '',
              originalValue: '',
            },
            {
              value: item.discounted_ten_year_avg_premium
                ? item.discounted_ten_year_avg_premium.toLocaleString('en-US')
                : parseInt(item.average_premium.toFixed(0)).toLocaleString('en-US'),
              key: this.$t('products.medical.md_10_yearAvgPremium'),
              topSubText: item.currency,
              toolTipHtml: this.get10YearAveragePremiumTooltip(),
              symbolValue: '',
              originalValue: item.discounted_ten_year_avg_premium
                ? parseInt(item.average_premium.toFixed(0)).toLocaleString('en-US')
                : '',
            },
            {
              value: item.promotion_prices[this.age]
                ? item.promotion_prices[this.age].toLocaleString('en-US')
                : parseInt(item.fyp.toFixed(0)).toLocaleString('en-US'),
              key: this.$t('products.medical.md_firstYearPremium'),
              topSubText: item.currency,
              toolTipHtml: this.getFirstYearPremiumTooltip(),
              symbolValue: '',
              originalValue: item.promotion_prices[this.age]
                ? parseInt(item.fyp.toFixed(0)).toLocaleString('en-US')
                : '',
            },
          ],
          mainButtonCTA: this.simpleItems.main_cta,
          showShortcut: this.simpleItems.shortcut,
        },
        detailSection: {
          details: [
            {
              id: 0,
              sectionTitle: this.$t('products.medical.md_overview'),
              keyValues: [
                {
                  key: this.$t('products.medical.md_overall_score'),
                  value: this.$formatScore(item.overall_score) + ' / 10',
                  keyToolTipHtml: this.getTooltipContentByKey('md_overall_score'),
                  expandableKeyValues: [],
                },
                {
                  key: this.$t('products.medical.md_medicalCoverageScore'),
                  value: this.$formatScore(item.medical_coverage_score) + ' / 10',
                  keyToolTipHtml: this.getTooltipContentByKey('md_medicalCoverageScore'),
                  expandableKeyValues: [],
                },
                {
                  key: this.$t('products.medical.md_averageCoverageofCommonMedicalCases'),
                  value: this.$formatPercentageDP(item.medical_coverage, 1),
                  keyToolTipHtml: this.getTooltipContentByKey('md_averageCoverageofCommonMedicalCases'),
                  expandableKeyValues: [],
                },
                {
                  key: this.$t('products.medical.md_cancerAverageCoverage'),
                  value: this.$formatPercentageDP(item.medical_cancer_coverage, 1),
                  keyToolTipHtml: this.getTooltipContentByKey('md_cancerAverageCoverage'),
                  expandableKeyValues: [],
                },
                {
                  key: this.$t('products.medical.md_special_feature_score'),
                  value: this.$formatScore(item.special_feature_score) + ' / 10',
                  keyToolTipHtml: this.getTooltipContentByKey('md_special_feature_score'),
                  expandableKeyValues: [],
                },
                {
                  key: this.$t('products.medical.md_firstYearPremium'),
                  value:
                    item.currency +
                    ' ' +
                    (item.promotion_prices[this.age]
                    ? item.promotion_prices[this.age].toLocaleString('en-US')
                    : parseInt(item.fyp.toFixed(0)).toLocaleString('en-US')),
                  originalValue: item.promotion_prices[this.age]
                    ? parseInt(item.fyp.toFixed(0)).toLocaleString('en-US')
                    : '',
                  keyToolTipHtml: this.getFirstYearPremiumTooltip(),
                  expandableKeyValues: [],
                },
                {
                  key: this.$t('products.medical.md_10_yearAvgPremium'),
                  value:
                    item.currency +
                    ' ' +
                    (item.discounted_ten_year_avg_premium
                      ? item.discounted_ten_year_avg_premium.toLocaleString('en-US')
                      : parseInt(item.average_premium.toFixed(0)).toLocaleString('en-US')),
                  originalValue: item.discounted_ten_year_avg_premium
                    ? parseInt(item.average_premium.toFixed(0)).toLocaleString('en-US')
                    : '',
                  keyToolTipHtml: this.get10YearAveragePremiumTooltip(),
                  expandableKeyValues: [],
                },
                {
                  key: this.$t('products.medical.md_deductible'),
                  value: this.$formatPrice(item.deductible),
                  keyToolTipHtml: '',
                  expandableKeyValues: [],
                },
                {
                  key: this.$t('products.medical.md_basePlanLimit'),
                  value: item.base_plan_annual_limit,
                  keyToolTipHtml: '',
                  expandableKeyValues: [],
                },
                {
                  key: this.$t('products.medical.md_lifetimeLimit'),
                  value: item.lifetime_limit,
                  keyToolTipHtml: '',
                  expandableKeyValues: [],
                },
                {
                  key: this.$t('products.medical.md_geographicalCoverage'),
                  value: item.geo_region,
                  keyToolTipHtml: '',
                  expandableKeyValues: [],
                },
                {
                  key: this.$t('products.medical.md_includeSMM'),
                  value: item.include_smm,
                  keyToolTipHtml: '',
                  expandableKeyValues: [],
                },
                {
                  key: this.$t('products.medical.md_sMMCo_pay'),
                  value: item.smm_co_pay,
                  keyToolTipHtml: '',
                  expandableKeyValues: [],
                },
                {
                  key: this.$t('products.medical.md_sMMGeneralLimits'),
                  value: item.smm_general_limits,
                  keyToolTipHtml: '',
                  expandableKeyValues: [],
                },
                {
                  key: this.$t('products.medical.md_sMMLifetimeLimits'),
                  value: item.smm_lifetime_limits,
                  keyToolTipHtml: '',
                  expandableKeyValues: [],
                },
              ],
            },
            {
              id: 1,
              sectionTitle: this.$t('products.medical.md_estimatedCoverageofselectedDiseases'),
              keyValues: item.medical_plan_coverages.map((coverage) => ({
                key: coverage?.attributes.medical_disease.data?.attributes.medical_disease_translations.data[0]?.attributes
                  .description,
                value: this.$formatPercentageDP(coverage?.attributes.estimated_coverage, 1),
                keyToolTipHtml: '',
                expandableKeyValues: [
                  {
                    key: this.$t('products.medical.md_estimatedTotalBill'),
                    value:
                      item.currency +
                      ' ' +
                      parseInt(coverage?.attributes.estimated_total.toFixed(0)).toLocaleString('en-US'),
                  },
                  {
                    key: this.$t('products.medical.md_estimatedCoverage'),
                    value:
                      item.currency +
                      ' ' +
                      parseInt(coverage?.attributes.estimated_coverage_amount.toFixed(0)).toLocaleString('en-US'),
                  },
                  {
                    key: this.$t('products.medical.md_estimatedOutofPocket'),
                    value:
                      item.currency +
                      ' ' +
                      parseInt(coverage?.attributes.estimated_out_of_pocket.toFixed(0)).toLocaleString('en-US'),
                  },
                ],
              })),
            },
            {
              id: 2,
              sectionTitle: this.$t('products.medical.md_benefitLimitsBasePlan'),
              keyValues: item.medical_ben_limit_values.map((ben) => {
                const code = ben?.attributes.medical_ben_limit.data?.attributes.medical_ben_limit_translations.data[0]?.attributes.code;

                let expandableKeyValues = ben?.attributes.medical_ben_sublimit_values.data.map((sub) => ({
                  key: sub?.attributes.medical_ben_sublimit.data?.attributes.medical_ben_sublimit_translations.data[0]?.attributes.description,
                  value: this.getBenSublimitValue(sub),
                })) || [];

                if (code === 'surgeons_fee') {
                  const hardcodedElement = {
                    key: this.$t('products.medical.md_surgeonsFeeRemarks'),
                    value: '',
                  };
                  expandableKeyValues = [hardcodedElement, ...expandableKeyValues];
                }

                return {
                  key: ben?.attributes.medical_ben_limit.data?.attributes.medical_ben_limit_translations.data[0]?.attributes.description,
                  value: this.getBenValue(ben),
                  keyToolTipHtml: '',
                  expandableKeyValues,
                };
              }),
            },
            {
              id: 3,
              sectionTitle: this.$t('products.medical.md_special_feature'),
              keyValues: [
                {
                  key: this.$t('products.medical.md_special_feature_score'),
                  value: this.$formatScore(item.special_feature_score) + ' / 10',
                  keyToolTipHtml: this.getTooltipContentByKey('md_special_feature_score'),
                  expandableKeyValues: [],
                },
                {
                  key: this.$t('products.medical.md_medicalInflationProtection_indicator'),
                  value: '',
                  keyToolTipHtml: '',
                  expandableKeyValues: [
                    {
                      key: this.$t('products.medical.md_noItemizedSubLimit'),
                      value: this.$formatSymbol(this.getSpecialFeatureValueByCode('no_sublimit_plan_ind'))
                    },
                    {
                      key: this.$t('products.medical.md_highAnnualLimit'),
                      value: this.$formatSymbol(this.getSpecialFeatureValueByCode('high_annual_limit_ind'))
                    },
                    {
                      key: this.$t('products.medical.md_noLifetimeLimit'),
                      value: this.$formatSymbol(this.getSpecialFeatureValueByCode('no_lifetime_limit_ind'))
                    },
                    {
                      key: this.$t('products.medical.md_guaranteedNoBenefitReductionAppliedtoAllVHISplans'),
                      value: this.$formatSymbol(this.getSpecialFeatureValueByCode('guar_no_benefit_reduction_ind'))
                    },
                  ],
                },
                {
                  key: this.$t('products.medical.md_additionalCoverage_indicator'),
                  value: '',
                  keyToolTipHtml: '',
                  expandableKeyValues: [
                    {
                      key: this.$t('products.medical.md_hospitalCash'),
                      value: this.$formatSymbol(this.getSpecialFeatureValueByCode('hospital_cash_ind')),
                      keyToolTipHtml: this.getTooltipContentByKey('md_hospitalCash')
                    },
                    {
                      key: this.$t('products.medical.md_hospicePalliativeCareCoverage'),
                      value: this.$formatSymbol(this.getSpecialFeatureValueByCode('hospice_palliative_care_ind'))
                    },
                    {
                      key: this.$t('products.medical.md_pregnancyComplicationsCoverage'),
                      value: this.$formatSymbol(this.getSpecialFeatureValueByCode('pregnancy_complications_ind'))
                    },
                    {
                      key: this.$t('products.medical.md_experimentalCancerTreatment'),
                      value: this.$formatSymbol(this.getSpecialFeatureValueByCode('experimental_cancer_treatment_ind'))
                    },
                    {
                      key: this.$t('products.medical.md_emergencyDentalBenefit'),
                      value: this.$formatSymbol(this.getSpecialFeatureValueByCode('emergency_dental_benefit_ind'))
                    },
                    {
                      key: this.$t('products.medical.md_shortWaitingPeriod'),
                      value: this.$formatSymbol(this.getSpecialFeatureValueByCode('short_waiting_period_ind'))
                    }
                  ],
                },
                {
                  key: this.$t('products.medical.md_hassleFreeArrangement_indicator'),
                  value: '',
                  keyToolTipHtml: '',
                  expandableKeyValues: [
                    {
                      key: this.$t('products.medical.md_cashlessArrangement'),
                      value: this.$formatSymbol(this.getSpecialFeatureValueByCode('cashless_arrangement_ind'))
                    },
                    {
                      key: this.$t('products.medical.md_personalCaseMgt'),
                      value: this.$formatSymbol(this.getSpecialFeatureValueByCode('personal_case_mgt_ind'))
                    },
                    {
                      key: this.$t('products.medical.md_healthHotline'),
                      value: this.$formatSymbol(this.getSpecialFeatureValueByCode('health_hotline_ind'))
                    },
                    {
                      key: this.$t('products.medical.md_mentalHealthHotline'),
                      value: this.$formatSymbol(this.getSpecialFeatureValueByCode('twentyfour_seven_mental_health_hotline_ind'))
                    },
                    {
                      key: this.$t('products.medical.md_psychiatricCounsellingOutpatient'),
                      value: this.$formatSymbol(this.getSpecialFeatureValueByCode('psychiatric_counselling_outpatient_ind'))
                    },
                    {
                      key: this.$t('products.medical.md_secondMedicalOpinionService'),
                      value: this.$formatSymbol(this.getSpecialFeatureValueByCode('second_medical_opinion_ind'))
                    },
                    {
                      key: this.$t('products.medical.md_internationalAssistanceService'),
                      value: this.$formatSymbol(this.getSpecialFeatureValueByCode('international_assistance_service_ind'))
                    },
                  ],
                },
                {
                  key: this.$t('products.medical.md_claimsRelated_indicator'),
                  value: '',
                  keyToolTipHtml: '',
                  expandableKeyValues: [
                    {
                      key: this.$t('products.medical.md_secondClaimCashAllowance'),
                      value: this.$formatSymbol(this.getSpecialFeatureValueByCode('second_claim_cash_allowance_ind')),
                      keyToolTipHtml: this.getTooltipContentByKey('md_secondClaimCashAllowance')
                    },
                    {
                      key: this.$t('products.medical.md_availabilityOfNoClaimBonus'),
                      value: this.$formatSymbol(this.getSpecialFeatureValueByCode('no_claim_bonus_ind'))
                    },
                    {
                      key: this.$t('products.medical.md_availabilityOfEClaim'),
                      value: this.$formatSymbol(this.getSpecialFeatureValueByCode('eclaim_ind'))
                    },
                  ]
                },
                {
                  key: this.$t('products.medical.md_wellnessAndOthers_indicator'),
                  value: '',
                  keyToolTipHtml: '',
                  expandableKeyValues: [
                    {
                      key: this.$t('products.medical.md_adjDeductibleWithoutReUnderwriteSpecificAge'),
                      value: this.$formatSymbol(this.getSpecialFeatureValueByCode('adj_deductible_without_re_underwrite_specific_age_ind'))
                    },
                    {
                      key: this.$t('products.medical.md_donorBenefits'),
                      value: this.$formatSymbol(this.getSpecialFeatureValueByCode('donor_benefits_for_organ_ind'))
                    },
                    {
                      key: this.$t('products.medical.md_compassionateDeathBenefit'),
                      value: this.$formatSymbol(this.getSpecialFeatureValueByCode('plan_compassionate_death_benefit_ind'))
                    },
                    {
                      key: this.$t('products.medical.md_freeBodyCheck'),
                      value: this.$formatSymbol(this.getSpecialFeatureValueByCode('body_check_ind'))
                    },
                    {
                      key: this.$t('products.medical.md_numberOfPolicyYearForFreeBodyCheck'),
                      value: this.processNonApplicableValue(this.getSpecialFeatureValueByCode('body_check_start_policy_year'))
                    },
                    {
                      key: this.$t('products.medical.md_frequencyOfFreeBodyCheck'),
                      value: this.processNonApplicableValue(this.getSpecialFeatureValueByCode('body_check_frequency'))
                    },
                    {
                      key: this.$t('products.medical.md_deductibleWaiverOnCancer'),
                      value: this.$formatSymbol(this.getSpecialFeatureValueByCode('deductible_waiver_cancer_ind'))
                    },
                    {
                      key: this.$t('products.medical.md_deductibleWaiverOnStrokeAndHeartDisease'),
                      value: this.$formatSymbol(this.getSpecialFeatureValueByCode('deductible_waiver_heart_stroke_ind'))
                    },
                    {
                      key: this.$t('products.medical.md_deductibleWaiverOnDiabetes'),
                      value: this.$formatSymbol(this.getSpecialFeatureValueByCode('deductible_waiver_kidney_diabetes_ind'))
                    },
                    {
                      key: this.$t('products.medical.md_deductibleWaiverOnOtherIllness'),
                      value: this.$formatSymbol(this.getSpecialFeatureValueByCode('deductible_waiver_others_ind'))
                    }
                  ],
                },
              ]
            },
            {
              id: 4,
              sectionTitle: this.$t('products.medical.md_averageAnnualPremium'),
              keyValues: [
                this.premiumAgeGroupData(item, 0, 5),
                this.premiumAgeGroupData(item, 6, 12),
                this.premiumAgeGroupData(item, 13, 18),
                this.premiumAgeGroupData(item, 19, 24),
                this.premiumAgeGroupData(item, 25, 34),
                this.premiumAgeGroupData(item, 35, 44),
                this.premiumAgeGroupData(item, 45, 54),
                this.premiumAgeGroupData(item, 55, 64),
                this.premiumAgeGroupData(item, 65, 74),
                this.premiumAgeGroupData(item, 75, 84),
                this.premiumAgeGroupData(item, 85, 94),
                this.premiumAgeGroupData(item, 95, 100),
                {
                  key: this.$t('products.medical.md_forRenewal'),
                  value: '',
                  keyToolTipHtml: '',
                  expandableKeyValues: [],
                },
              ],
            },
          ],
          promotion: {
            category:
              item.product_promotion?.attributes?.product_promotion_translations?.data[0]?.attributes
                ?.PromotionCategory,
            title:
              item.product_promotion?.attributes?.product_promotion_translations?.data[0]?.attributes
                ?.PromotionTitle,
            ctaButtonLink:
              item.product_promotion?.attributes?.product_promotion_translations?.data[0]?.attributes
                ?.CTAButtonLink,
            ctaButtonText:
              item.product_promotion?.attributes?.product_promotion_translations?.data[0]?.attributes
                ?.CTAButtonText,
            promotionId: item.product_promotion?.id,
            scenarioId: item.scenarioId,
            lastCall: item.promotion_last_call

          },
        },
      };
    },
  },

  methods: {
    getScore(product) {
      return product.overall_score?.toFixed(1);
    },

    /*
    getMedicalInflation(inflations, code) {
      let tmpInflation = null;
      tmpInflation = inflations.filter((inflation) => {
        return code === inflation?.attributes.ip_code;
      });
      return {
        label:
          tmpInflation[0]?.attributes.medical_inflation.data?.attributes.medical_inflation_translations.data[0]?.attributes
            .description,
        value: tmpInflation[0]?.attributes.ip_value,
      };
    },
    */

    getBenValue(ben) {
      if (this.currentLocale === 'en') {
        return ben?.attributes.localizations.data[0]?.attributes.bl_value;
      } else {
        return ben?.attributes.bl_value;
      }
    },

    getBenSublimitValue(sub) {
      if (!isNaN(sub?.attributes.bsl_value)) {
        const isSpecialBSLValue = (value) => {
          const specialValues = ["1.0", "0.5", "0.0", 1.0, 0.5, 0.0];
          return specialValues.includes(value);
        }
        if (isSpecialBSLValue(sub?.attributes.bsl_value)) {
          return this.$formatPercentageDP(sub?.attributes.bsl_value, 0);
        } else {
          return this.$formatPercentageDP(sub?.attributes.bsl_value, 1);
        }
      }
      if (this.currentLocale === 'en') {
        return sub?.attributes.localizations.data[0]?.attributes.bsl_value;
      } else {
        return sub?.attributes.bsl_value;
      }
    },

    getSMMValue(smm) {
      if (this.currentLocale === 'en') {
        return smm?.attributes.localizations.data[0]?.attributes.smm_value;
      } else {
        return smm?.attributes.smm_value;
      }
    },

    getPremium(premium, startAge) {
      const endAge = startAge + 9;
      const selectedAge = () => {
        return premium.data.filter((agePremium) => {
          return agePremium?.attributes.age >= startAge && agePremium?.attributes.age <= endAge;
        });
      };

      return selectedAge().map((agePremium) => ({
        age: agePremium?.attributes.age,
        value: agePremium?.attributes.price,
      }));
    },

    premiumAgeGroupData(item, fromAge, toAge) {
      const selectedAge = () => {
        // return item.premium.filter(agePremium => {return agePremium?.attributes.age >= fromAge && agePremium?.attributes.age <= toAge});
        const premiums = item.premium.filter((agePremium) => {
          return agePremium?.attributes.age >= fromAge && agePremium?.attributes.age <= toAge;
        });
        return premiums.map((premium) => {
          const price = item.promotion_prices[premium?.attributes.age]
            ? item.promotion_prices[premium?.attributes.age]
            : premium?.attributes.price;
          // const originalPrice = item.promotion_prices[premium?.attributes.age]
            // ? premium?.attributes.price
            // : '';
            const originalPrice = premium?.attributes.price;
          return { attributes: { price, age: premium?.attributes.age, originalPrice } };
        });
      };

      const avgSelected = () => {
        const totalPrice = 0;
        const priceOnly = selectedAge().map((x) => x?.attributes.price);
        const sumWithInitial = priceOnly.reduce((accumulator, currentValue) => accumulator + currentValue, totalPrice);
        return sumWithInitial / selectedAge().length;
      };

      const avgOriginalSelected = () => {
        const totalPrice = 0;
        const priceOnly = selectedAge().map((x) => x?.attributes.originalPrice);
        const sumWithInitial = priceOnly.reduce((accumulator, currentValue) => accumulator + currentValue, totalPrice);
        return sumWithInitial / selectedAge().length;
      };

      const getAge = (age, lblAge) => {
        return this.$i18n.locale === 'en' ? lblAge + ' ' + age : age + ' ' + lblAge;
      };

      const appendAsterisk = (age, maxIssueAge) => {
        return age >= maxIssueAge ? '*' : '';
      };

      return {
        key: this.$t('products.medical.md_ageRange').replace('$from', fromAge).replace('$to', toAge),
        value: item.currency + ' ' + parseInt(avgSelected().toFixed(0)).toLocaleString('en-US'),
        originalValue: avgOriginalSelected()!==avgSelected()?item.currency + ' ' + parseInt(avgOriginalSelected().toFixed(0)).toLocaleString('en-US'):'',
        keyToolTipHtml: '',
        expandableKeyValues: selectedAge().map((agePremium) => ({
          key: getAge(agePremium?.attributes.age, this.$t('products.ageUnit')),
          value:
            item.currency +
            ' ' +
            parseInt(agePremium?.attributes.price).toLocaleString('en-US') +
            appendAsterisk(agePremium?.attributes.age, item.max_issue_age),
          originalValue:
            agePremium?.attributes.price !== agePremium?.attributes.originalPrice ?
            item.currency +
            ' ' +
            parseInt(agePremium?.attributes.originalPrice).toLocaleString('en-US') +
            appendAsterisk(agePremium?.attributes.age, item.max_issue_age):
            '',
          keyToolTipHtml: '',
          expandableKeyValues: [],
        })),
      };
    },

    /*
    additionalFeaturesKVs(item) {
      const firstArray = [
        {
          key: this.$t('products.medical.md_additionalCoverage'),
          value: item.additional_features,
          keyToolTipHtml: this.getTooltipContentByKey('md_additionalCoverage'),
          expandableKeyValues: [],
        },
      ];

      const secondArray = item.medical_add_feature_values.map((add) => {
        let mTooltopHtml = '';

        switch (add?.attributes.af_code) {
          case 'add_hospital_cash':
            mTooltopHtml = this.getTooltipContentByKey('md_hospitalCash');
            break;

          case 'add_hospice_palliative_care_coverage':
            mTooltopHtml = this.getTooltipContentByKey('md_hospicePalliativeCareCoverage');
            break;

          case 'add_long_term_post_hospitalisation_outpatient_cover':
            mTooltopHtml = this.getTooltipContentByKey('md_long_termPostHospitalisationOutpatientCover');
            break;

          case 'add_short_waiting_perid':
            mTooltopHtml = this.getTooltipContentByKey('md_shortWaitingPeriod');
            break;

          case 'add_pregnancy_complications_coverage':
            mTooltopHtml = this.getTooltipContentByKey('md_pregnancyComplicationsCoverage');
            break;
        }

        return {
          key: add?.attributes.medical_add_feature.data?.attributes.medical_add_feature_translations.data[0]?.attributes
            .description,
          value: add?.attributes.af_value,
          keyToolTipHtml: mTooltopHtml,
          expandableKeyValues: [],
        };
      });

      return firstArray.concat(secondArray);
    },
    */

    getSpecialFeatureValueByCode(code) {
      return this.simpleItems.medical_special_features?.find(item => item.attributes.code === code)?.attributes?.value;
    },

    processNonApplicableValue(value) {
      if (value === '-1') {
        return this.$t('not_applicable');
      }

      return value;
    },

    getTooltipContentByKey(mKey) {
      return this.medicalTooltips?.Medical?.ToolTips?.find(({ Key }) => Key === mKey)?.ToolTipContent ?? '';
    },

    getGenderValue() {
      return this.gender === '1' ? this.$t('products.male') : this.$t('products.female');
    },

    getFirstYearPremiumTooltip() {
      const tooltip = this.getTooltipContentByKey('md_firstYearPremium');
      return tooltip.replace('{replace_age}', this.age.toString()).replace('{replace_gender}', this.getGenderValue());
    },

    get10YearAveragePremiumTooltip() {
      const tooltip = this.getTooltipContentByKey('md_10_yearAvgPremium');
      return tooltip
        .replace('{replace_gender}', this.getGenderValue())
        .replace('{replace_age_from}', this.age.toString())
        .replace('{replace_age_to}', (parseInt(this.age) + 9).toString());
    },

    getDecoderCTA(item, promotionCTALink, promotion = null) {
      const planName = item.attributes.medical_plan.data.attributes.medical_plan_translations.data[0].attributes.name;
      const insurer = item.attributes.medical_plan.data.attributes.product.data.attributes.insurance_company.data.attributes.insurance_company_translations.data[0].attributes.name;
      const decoderCTAWhatsappLink = this.$generateWhatsappCTALink(this.$t('products.medical.md_categoryName'), insurer, planName);
      const showCTA = item.attributes.show_cta;
      const saleable = item.attributes.medical_plan.data.attributes.saleable;
      const productWebsite = item.attributes.medical_plan.data.attributes.medical_plan_translations.data[0].attributes.product_website;
      const salesLink = process.env.SALES_EXTERNAL_URL + '/' + (this.currentLocale === 'en' ? 'en' : 'tc') + '/purchase?type=vhis&id=' + item.attributes.scenario_code + (promotion?.id ? '&promotionId=' + promotion?.id : '');

      if (!showCTA) return null;

      if (saleable) {
        return {
          text: this.$t('products.purchase_now'),
          link: salesLink,
          popupData: {
            applicationLink: salesLink,
            whatsappLink: decoderCTAWhatsappLink
          },
          type: 'popup'
        };
      } else if (promotionCTALink) {
        return {
          text: this.$t('products.product_website'),
          link: promotionCTALink,
        };
      } else if (productWebsite) {
        return {
          text: this.$t('products.product_website'),
          link: productWebsite,
          type: 'product-website',
        };
      } else {
        return {
          text: '',
          link: decoderCTAWhatsappLink,
        };
      }
    },
  },
};
